// src/flags.js
import Rox from 'rox-browser';

function safeGetString(conf, fallback = '') {
  try {
    const v = conf.getValue();
    return (typeof v === 'string') ? v : fallback;
  } catch (e) {
    console.warn('[FM] getValue() failed for string config:', e);
    return fallback;
  }
}

// ---- Declare all flags here (namespace: vehiclestack.ui) ----
export const flags = {
  // Core feature visibility
  showTelemetry:   new Rox.Flag(true),
  showDiagnostics: new Rox.Flag(true),
  showHealthPanel: new Rox.Flag(true),

  // Admin features
  adminUsers: new Rox.Flag(false),

  // Welcome/branding
  welcomeMessage: new Rox.RoxString('Welcome to VehicleStack', []),

  // Observability
  logLevel: new Rox.RoxString('info', ['off','error','warn','info','debug']),
};

// Register under one namespace
Rox.register('vehiclestack.ui', flags);

// Internal snapshot (null until we evaluate AFTER setup)
let _snapshot = null;
const listeners = new Set();

export function getFlagsSnapshot() { return _snapshot || {}; }
export function subscribeFlags(cb) { listeners.add(cb); return () => listeners.delete(cb); }

function buildSnapshot() {
  // Single evaluation point
  return {
    showTelemetry:   flags.showTelemetry.isEnabled(),
    showDiagnostics: flags.showDiagnostics.isEnabled(),
    showHealthPanel: flags.showHealthPanel.isEnabled(),
    adminUsers:      flags.adminUsers.isEnabled(),
    welcomeMessage:  safeGetString(flags.welcomeMessage, 'Welcome to VehicleStack'),
    logLevel:        safeGetString(flags.logLevel, 'info'),
  };
}

/** Optional: warm caches once after setup */
export function primeFlags() {
  try {
    void flags.showTelemetry.isEnabled();
    void flags.showDiagnostics.isEnabled();
    void flags.showHealthPanel.isEnabled();
    void flags.adminUsers.isEnabled();
    void flags.welcomeMessage.getValue();
    void flags.logLevel.getValue();
  } catch (e) {
    console.warn('[FM] primeFlags error', e);
  }
}

/** Rebuild snapshot AFTER Rox.setup() or when Rox.fetch() completes */
export function setFlagsSnapshot(reason = 'update') {
  _snapshot = buildSnapshot();
  console.log('[FM] flags updated:', reason, { ..._snapshot });
  for (const fn of listeners) {
    try { fn(reason, getFlagsSnapshot()); } catch {}
  }
}

/** One-shot forced evaluation AFTER setup, to ensure impressions are sent */
export function evaluateAllForImpressions() {
  try {
    void flags.showTelemetry.isEnabled();
    void flags.showDiagnostics.isEnabled();
    void flags.showHealthPanel.isEnabled();
    void flags.adminUsers.isEnabled();
    void flags.welcomeMessage.getValue();
    void flags.logLevel.getValue();
    console.log('[FM] evaluateAllForImpressions: done');
  } catch (e) {
    console.warn('[FM] evaluateAllForImpressions error', e);
  }
}

/** Expose current user to FM for targeting */
export function wireUserContext() {
  const getUser = () => {
    try { return JSON.parse(localStorage.getItem('vehicle.user') || 'null') || {}; }
    catch { return {}; }
  };

  Rox.setCustomStringProperty('email', () => String(getUser().email || ''));
  Rox.setCustomStringProperty('userId',   () => String(getUser().id || getUser().user_id || ''));
  Rox.setCustomStringProperty('username', () => String(getUser().username || ''));
  Rox.setCustomStringProperty('fullName', () => String(getUser().name || getUser().full_name || ''));

  Rox.setCustomStringProperty('roles', () => {
    const roles = Array.isArray(getUser().roles) ? getUser().roles : [];
    return roles.join(',');
  });

  Rox.setCustomBooleanProperty('isAdmin', () => {
    const roles = Array.isArray(getUser().roles) ? getUser().roles : [];
    return roles.includes('admin');
  });

  console.log('[FM] user context wired');
}

/** Ask Unify to re-evaluate rules (e.g., after login/logout) */
export async function refreshFlags() {
  try {
    if (typeof Rox?.fetch === 'function') {
      console.log('[FM] refreshFlags → Rox.fetch()');
      await Rox.fetch();
      evaluateAllForImpressions();
      setFlagsSnapshot('Rox.fetch()');
    }
  } catch (e) {
    console.warn('[FM] refreshFlags failed', e);
  }
}
